import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Button,
  Grid,
  Column,
  Tile,
  TextInput,
  PasswordInput,
  InlineNotification,
  RadioButton,
  RadioButtonGroup,
  Loading,
} from '@carbon/react';
import { ArrowLeft } from '@carbon/icons-react';
import { useTheme } from '../context/ThemeContext';
import {
  LILYGO_SOFTAP,
  SOFTAP_SENT_ALERT,
  canAttemptSoftApFromBrowser,
  softApBrowserLimitationMessage,
  softApUserFromMac,
  softApSsidFromMac,
  uplinkCredsFromGateway,
  fetchGatewayById,
  fetchSoftApStatus,
  commissionWifiOverSoftAp,
  commissionLteOverSoftAp,
  pushCloudUplinkOverSoftAp,
} from '../services/lilyGoGatewayService';
import { getGatewayProductFamily } from '../utils/gatewayProductLabel';

/**
 * SoftAP commission after LGateway claim — mirrors LilyGoCommissionScreen.
 * On HTTPS production sites, SoftAP HTTP is blocked (mixed content); we show
 * clear guidance and still offer the form for localhost / HTTP deployments.
 */
const LilyGoCommissionPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const bgPrimary = isDark ? '#161616' : '#ffffff';
  const bgSecondary = isDark ? '#262626' : '#f4f4f4';
  const textPrimary = isDark ? '#f4f4f4' : '#161616';
  const textSecondary = isDark ? '#c6c6c6' : '#525252';
  const borderColor = isDark ? '#393939' : '#e0e0e0';

  const { gatewayId, deviceApiKey, macAddress: macParam } = location.state || {};

  const [mode, setMode] = useState('wifi');
  const [ssid, setSsid] = useState('');
  const [wifiPassword, setWifiPassword] = useState('');
  const [apn, setApn] = useState('');
  const [lteUser, setLteUser] = useState('');
  const [ltePassword, setLtePassword] = useState('');
  const [softApUser, setSoftApUser] = useState(() => softApUserFromMac(macParam) || 'chillverify_');
  const [softApPassword, setSoftApPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [softApStatus, setSoftApStatus] = useState(null);
  const [cachedUplink, setCachedUplink] = useState(null);
  const [macAddress, setMacAddress] = useState(macParam || '');
  const [gatewayFamily, setGatewayFamily] = useState(null);

  const softApBlocked = !canAttemptSoftApFromBrowser();
  const softApBlockMsg = softApBrowserLimitationMessage();

  const expectedSsid = useMemo(
    () => softApStatus?.softap_ssid || softApSsidFromMac(macAddress || softApStatus?.mac),
    [softApStatus, macAddress],
  );

  useEffect(() => {
    if (!gatewayId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const gw = await fetchGatewayById(gatewayId);
        if (cancelled) return;
        if (gw?.mac_address) setMacAddress(gw.mac_address);
        setGatewayFamily(getGatewayProductFamily(gw));
        setCachedUplink(uplinkCredsFromGateway(gw, deviceApiKey));
        const u = softApUserFromMac(gw?.mac_address);
        if (u) setSoftApUser(u);
      } catch {
        if (!cancelled) {
          setCachedUplink(deviceApiKey ? { device_api_key: deviceApiKey } : null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [gatewayId, deviceApiKey]);

  useEffect(() => {
    if (gatewayFamily === 'MGateway' && mode === 'lte') {
      setMode('wifi');
    }
  }, [gatewayFamily, mode]);

  if (!gatewayId) {
    return (
      <div style={{ backgroundColor: bgPrimary, minHeight: '100vh', padding: '48px 0' }}>
        <Grid>
          <Column lg={{ span: 10, offset: 3 }} md={8} sm={4}>
            <InlineNotification
              kind="error"
              title="Missing gateway"
              subtitle="Start from Add Gateway → Claim LGateway or MGateway, then Configure network."
              hideCloseButton
            />
            <Button kind="primary" style={{ marginTop: 16 }} onClick={() => navigate('/dashboard/gateways/add')}>
              Add Gateway
            </Button>
          </Column>
        </Grid>
      </div>
    );
  }

  const softApAuth = () => ({
    username: softApUser.trim(),
    password: softApPassword,
  });

  const buildUplink = () => {
    if (cachedUplink?.mqtt_username && cachedUplink?.mqtt_password) return cachedUplink;
    return uplinkCredsFromGateway(null, deviceApiKey);
  };

  const checkSoftAp = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    if (softApBlocked) {
      setError(softApBlockMsg);
      setLoading(false);
      return;
    }
    const status = await fetchSoftApStatus();
    setSoftApStatus(status);
    setLoading(false);
    if (!status) {
      setError(
        `SoftAP not reachable. On this device, join Wi‑Fi “${expectedSsid || `${LILYGO_SOFTAP.ssidPrefix}<serial>`}” with the SoftAP password from the owner card, then retry.`,
      );
      return;
    }
    if (status.softap_user) setSoftApUser(String(status.softap_user));
    setSuccess('Connected to ChillVerify SoftAP.');
  };

  const applyWifi = async () => {
    setError('');
    setSuccess('');
    if (!ssid.trim()) {
      setError('Enter your site / home Wi‑Fi name (not ChillVerify SoftAP).');
      return;
    }
    if (!softApUser.trim() || !softApPassword) {
      setError('Enter SoftAP username and password from the owner card.');
      return;
    }
    if (softApBlocked) {
      setError(softApBlockMsg);
      return;
    }
    setLoading(true);
    const uplink = buildUplink();
    await pushCloudUplinkOverSoftAp(uplink, { auth: softApAuth() });
    const result = await commissionWifiOverSoftAp(
      { ...uplink, ssid: ssid.trim(), password: wifiPassword, preferred_link: 'wifi' },
      { auth: softApAuth() },
    );
    setLoading(false);
    if (!result.success) {
      setError(result.error || 'Wi‑Fi commission failed');
      return;
    }
    setSuccess(SOFTAP_SENT_ALERT);
  };

  const applyLte = async () => {
    setError('');
    setSuccess('');
    if (!apn.trim()) {
      setError('APN is required for LTE.');
      return;
    }
    if (!softApUser.trim() || !softApPassword) {
      setError('Enter SoftAP username and password from the owner card.');
      return;
    }
    if (softApBlocked) {
      setError(softApBlockMsg);
      return;
    }
    setLoading(true);
    const uplink = buildUplink();
    await pushCloudUplinkOverSoftAp(uplink, { auth: softApAuth() });
    const result = await commissionLteOverSoftAp(
      {
        ...uplink,
        apn: apn.trim(),
        user: lteUser,
        password: ltePassword,
        preferred_link: 'lte',
      },
      { auth: softApAuth() },
    );
    setLoading(false);
    if (!result.success) {
      setError(result.error || 'LTE commission failed');
      return;
    }
    setSuccess(SOFTAP_SENT_ALERT);
  };

  return (
    <div style={{ backgroundColor: bgPrimary, minHeight: '100vh', padding: '48px 0' }}>
      <Grid>
        <Column lg={{ span: 10, offset: 3 }} md={8} sm={4}>
          <Button
            kind="ghost"
            renderIcon={ArrowLeft}
            onClick={() => navigate(gatewayId ? `/dashboard/gateways/${gatewayId}` : '/dashboard/gateways')}
            style={{ marginBottom: '24px' }}
          >
            Back
          </Button>
          <h1 style={{ color: textPrimary, fontSize: '1.75rem', fontWeight: 600, marginBottom: '8px' }}>
            Configure network
          </h1>
          <p style={{ color: textSecondary, marginBottom: '24px', lineHeight: 1.5 }}>
            Join <strong>{expectedSsid || 'ChillVerify_&lt;serial&gt;'}</strong> on this device
            (SoftAP at {LILYGO_SOFTAP.baseUrl}), then send site Wi‑Fi or LTE. SoftAP password is on the owner card.
          </p>

          {softApBlocked && (
            <InlineNotification
              kind="warning"
              title="Browser SoftAP limit"
              subtitle={softApBlockMsg}
              hideCloseButton
              style={{ marginBottom: '16px' }}
            />
          )}

          {error && (
            <InlineNotification
              kind="error"
              title="Error"
              subtitle={error}
              onClose={() => setError('')}
              style={{ marginBottom: '16px' }}
            />
          )}
          {success && (
            <InlineNotification
              kind="success"
              title="Done"
              subtitle={success}
              onClose={() => setSuccess('')}
              style={{ marginBottom: '16px' }}
            />
          )}

          <Tile style={{ backgroundColor: bgSecondary, border: `1px solid ${borderColor}`, padding: '24px', marginBottom: '16px' }}>
            <h3 style={{ color: textPrimary, marginTop: 0 }}>SoftAP login</h3>
            <div style={{ marginBottom: 12 }}>
              <TextInput
                id="softApUser"
                labelText="SoftAP username"
                value={softApUser}
                onChange={(e) => setSoftApUser(e.target.value)}
                helperText="Usually chillverify_<last6 of MAC>"
              />
            </div>
            <div style={{ marginBottom: 12 }}>
              <PasswordInput
                id="softApPassword"
                labelText="SoftAP password"
                value={softApPassword}
                onChange={(e) => setSoftApPassword(e.target.value)}
              />
            </div>
            <Button kind="tertiary" size="sm" onClick={checkSoftAp} disabled={loading}>
              {loading ? 'Checking…' : 'Check SoftAP status'}
            </Button>
            {softApStatus && (
              <pre
                style={{
                  marginTop: 12,
                  fontSize: 12,
                  color: textSecondary,
                  overflow: 'auto',
                  maxHeight: 160,
                }}
              >
                {JSON.stringify(softApStatus, null, 2)}
              </pre>
            )}
          </Tile>

          <Tile style={{ backgroundColor: bgSecondary, border: `1px solid ${borderColor}`, padding: '24px' }}>
            <RadioButtonGroup
              name="link-mode"
              legendText="Uplink"
              valueSelected={mode}
              onChange={(v) => setMode(v)}
            >
              <RadioButton id="wifi" value="wifi" labelText="Site Wi‑Fi" />
              {gatewayFamily === 'LGateway' && (
                <RadioButton id="lte" value="lte" labelText="LTE (LGateway cellular)" />
              )}
            </RadioButtonGroup>

            {mode === 'wifi' ? (
              <>
                <div style={{ marginTop: 16, marginBottom: 12 }}>
                  <TextInput
                    id="ssid"
                    labelText="Site Wi‑Fi SSID *"
                    value={ssid}
                    onChange={(e) => setSsid(e.target.value)}
                    helperText="Your home / site network — not ChillVerify SoftAP"
                  />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <PasswordInput
                    id="wifiPassword"
                    labelText="Site Wi‑Fi password"
                    value={wifiPassword}
                    onChange={(e) => setWifiPassword(e.target.value)}
                  />
                </div>
                <Button kind="primary" onClick={applyWifi} disabled={loading || softApBlocked}>
                  {loading ? 'Sending…' : 'Send Wi‑Fi via SoftAP'}
                </Button>
              </>
            ) : (
              <>
                <div style={{ marginTop: 16, marginBottom: 12 }}>
                  <TextInput id="apn" labelText="APN *" value={apn} onChange={(e) => setApn(e.target.value)} />
                </div>
                <div style={{ marginBottom: 12 }}>
                  <TextInput id="lteUser" labelText="LTE user (optional)" value={lteUser} onChange={(e) => setLteUser(e.target.value)} />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <PasswordInput
                    id="ltePassword"
                    labelText="LTE password (optional)"
                    value={ltePassword}
                    onChange={(e) => setLtePassword(e.target.value)}
                  />
                </div>
                <Button kind="primary" onClick={applyLte} disabled={loading || softApBlocked}>
                  {loading ? 'Sending…' : 'Send LTE via SoftAP'}
                </Button>
              </>
            )}

            {loading && (
              <div style={{ marginTop: 16 }}>
                <Loading withOverlay={false} description="Talking to SoftAP…" />
              </div>
            )}
          </Tile>

          <div style={{ marginTop: 24 }}>
            <Button kind="secondary" onClick={() => navigate(`/dashboard/gateways/${gatewayId}`)}>
              Skip — open gateway
            </Button>
          </div>
        </Column>
      </Grid>
    </div>
  );
};

export default LilyGoCommissionPage;
